import React, {useState} from 'react';
import {Accordion, AccordionDetails, AccordionSummary, Grid, Typography, useTheme} from "@mui/material";
import {useFormContext} from "react-hook-form";
import {useTranslation} from "react-i18next";
import {ExpandMore} from "@mui/icons-material";
import {FormAutocomplete, FormTextField} from '../../../common/form-fields';
import {SelectElementModelFE} from '../../../api/types/generic';
import {FormProductPlan} from '../../../api/types/productPlan';
import {useProductPlanRepository} from '../../../api/repositories/ProductPlanRepository';
import {useRecoilState} from 'recoil';
import {ratePlanAtom} from '../../../state/productPlan';
import {RentalPeriodEntityResponse} from '../../../api/types/rentalPeriods';

const ProductPlanRate = () => {

    // Use State
    const [rateExpanded, setRateExpanded] = useState<boolean>(false);

    // Use Theme
    const theme = useTheme();

    // Use Translation
    const {t} = useTranslation(["translation", "pricing"]);

    // Use Repository Hooks
    const {getCarGroupRentalPeriodByRate, getPriceAndExtraKm} = useProductPlanRepository();

    // Use Form Context
    const {register, resetField} = useFormContext<FormProductPlan>();

    const form = useFormContext<FormProductPlan>();
    const watchAllForm = form.watch();

    //Use Recoil
    const [ratePlan] = useRecoilState(ratePlanAtom);

    //Use State
    const [carGroup, setCarGroup] = useState<string[] | null | undefined>([])
    const [rentalPeriod, setRentalPeriod] = useState<RentalPeriodEntityResponse[]>([])

    //Onchange Rate plan
    const handleChangeRatePlan = async (value: SelectElementModelFE) => {
        if (value) {
            form.setValue("productPlan.ratePlan.rate", value)
            const carGroupRentalPeriodByRateResponse = await getCarGroupRentalPeriodByRate(value.code as number);
            setCarGroup(carGroupRentalPeriodByRateResponse.carGroups)
            setRentalPeriod(carGroupRentalPeriodByRateResponse.rentalPeriods)
        } else {
            form.setValue("productPlan.ratePlan.rate", value)
            resetField("productPlan.ratePlan.extraKm")
            resetField("productPlan.ratePlan.price")
            resetField("productPlan.ratePlan.rentalPeriod")
            resetField("productPlan.ratePlan.carGroup")
        }
    }

    //OnChange Car Groups
    const handleChangeCarGroup = (value: string) => {
        if (value) {
            form.setValue("productPlan.ratePlan.carGroup", value)
        } else {
            form.setValue("productPlan.ratePlan.carGroup", value)
            resetField("productPlan.ratePlan.extraKm")
            resetField("productPlan.ratePlan.price")
            resetField("productPlan.ratePlan.rentalPeriod")
        }
    }

    //Onchange Rental Periods
    const handleChangeRentalPeriods = async (value: SelectElementModelFE) => {
        if (value) {
            const rateId = form.getValues("productPlan.ratePlan.rate.code") as number;
            const carGroup = form.getValues("productPlan.ratePlan.carGroup") as string;
            form.setValue("productPlan.ratePlan.rentalPeriod", value)
            const priceAndExtraKmResponse = await getPriceAndExtraKm(rateId, carGroup, value.code as number)
            form.setValue("productPlan.ratePlan.price", priceAndExtraKmResponse.amount / 100);
            form.setValue("productPlan.ratePlan.extraKm", priceAndExtraKmResponse.extraKm / 100);
        } else {
            form.setValue("productPlan.ratePlan.rentalPeriod", value)
            resetField("productPlan.ratePlan.extraKm")
            resetField("productPlan.ratePlan.price")
        }
    }

    return (
        <Accordion
            expanded={rateExpanded}
            onChange={(e, expanded) => setRateExpanded(expanded)}
        >
            <AccordionSummary expandIcon={<ExpandMore/>}>
                <Typography sx={{color: theme.palette.primary.dark}}>
                    {"RATE PLAN"}
                </Typography>
            </AccordionSummary>
            <AccordionDetails>
                {/* RATE BASE */}
                <Grid container rowSpacing={2} columnSpacing={2}>
                    <Grid item xs={12} md={3}>
                        <FormAutocomplete
                            {...register("productPlan.ratePlan.rate")}
                            label={`${t("pricing:rate.title")}*`}
                            options={ratePlan.map((element) => {
                                return {
                                    code: element.id,
                                    label: element.name
                                }
                            }) || []}
                            isOptionEqualToValue={(
                                option: SelectElementModelFE,
                                value: SelectElementModelFE
                            ) => option?.code === value?.code}
                            handleChange={handleChangeRatePlan as Function}
                            readOnly={ratePlan.length === 0}
                        />
                    </Grid>
                    {/* CAR GROUPS */}
                    <Grid item xs={12} md={3}>
                        <FormAutocomplete
                            {...register("productPlan.ratePlan.carGroup")}
                            label={`${t("pricing:excessGroup.form.labels.carGroup")}*`}
                            options={carGroup || []}
                            isOptionEqualToValue={(
                                option: string,
                                value: string
                            ) => option === value}
                            readOnly={!watchAllForm.productPlan.ratePlan.rate}
                            handleChange={handleChangeCarGroup}
                        />
                    </Grid>
                    {/* RENTAL PERIODS */}
                    <Grid item xs={12} md={3}>
                        <FormAutocomplete
                            {...register("productPlan.ratePlan.rentalPeriod")}
                            label={`${t("pricing:ancillaryMatrixInsert.form.labels.rentalPeriods")}*`}
                            options={rentalPeriod?.map((element: RentalPeriodEntityResponse) => {
                                return {
                                    code: element.id,
                                    label: element.name
                                }
                            }) || []}
                            isOptionEqualToValue={(
                                option: SelectElementModelFE,
                                value: SelectElementModelFE
                            ) => option?.code === value?.code}
                            readOnly={!watchAllForm.productPlan.ratePlan.carGroup}
                            handleChange={handleChangeRentalPeriods as Function}
                        />
                    </Grid>
                    {/* PRICE */}
                    <Grid item xs={12} md={1.5}>
                        <FormTextField
                            {...register("productPlan.ratePlan.price")}
                            label={`${t("pricing:productPlan.price")}`}
                            type={"text"}
                            readOnly={true}
                        />
                    </Grid>
                    {/* EXTRA KM */}
                    <Grid item xs={12} md={1.5}>
                        <FormTextField
                            {...register("productPlan.ratePlan.extraKm")}
                            label={"Extra KM"}
                            type={"text"}
                            readOnly={true}
                        />
                    </Grid>
                </Grid>
            </AccordionDetails>
        </Accordion>
    );

}

export default ProductPlanRate;